import { useEffect, useRef, useState } from 'react'
import { lookupAddress, type AddressSuggestion } from '../../utils/addressLookup'
import type { Address } from '../../core/types/requests'

export type LookupState = 'idle' | 'loading' | 'found' | 'notFound'

const DEBOUNCE_MS = 350

/**
 * Vult straat en plaats aan zodra een Nederlandse postcode en een huisnummer zijn ingevuld.
 * Overschrijft nooit wat ze zelf typte: alleen lege velden, of velden die eerder door de
 * aanvulling zijn gevuld. Een oudere zoekvraag wordt afgebroken als ze verder typt.
 */
export function useAddressLookup(address: Address, fill: (s: AddressSuggestion) => void, enabled = true): LookupState {
  const [state, setState] = useState<LookupState>('idle')
  const last = useRef<AddressSuggestion | null>(null)
  const fillRef = useRef(fill)
  fillRef.current = fill
  const addressRef = useRef(address)
  addressRef.current = address

  const { country, postcode, houseNumber } = address
  useEffect(() => {
    if (!enabled || country !== 'NL' || !postcode.trim() || !houseNumber.trim()) {
      setState('idle')
      return
    }
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      setState('loading')
      void lookupAddress(postcode, houseNumber, controller.signal).then((found) => {
        if (controller.signal.aborted) return
        if (!found) {
          setState('notFound')
          return
        }
        const current = addressRef.current
        const ours = (value: string, key: keyof AddressSuggestion) => !value.trim() || value === last.current?.[key]
        if (ours(current.street, 'street') && ours(current.city, 'city')) fillRef.current(found)
        last.current = found
        setState('found')
      })
    }, DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [enabled, country, postcode, houseNumber])

  return state
}
